/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import { FC, ReactNode } from "react";
import { format } from "date-fns";
import { FormattedMessage } from "react-intl";
import ReactHtmlParser from "react-html-parser";

import "./TrackingHistoryItem.scss";
import { replaceLinks } from "helpers/urlHelpers";
import { ReactComponent as PackageIcon } from "assets/images/iconsV2/package.svg";
import { ReactComponent as CargoShipIcon } from "../../../../../../../assets/images/iconsV2/cargo-ship.svg";
import { ReactComponent as TruckIcon } from "../../../../../../../assets/images/iconsV2/truck-01.svg";
import { ReactComponent as PlaneIcon } from "../../../../../../../assets/images/iconsV2/plane.svg";
import TrackingException, { TrackingExceptionProps } from "../TrackingException/TrackingException";

export type TrackingHistoryItemProps = {
  /**
   * The date of the tracking update.
   */
  date: string;
  /**
   * The message of the tracking update.
   */
  message: string;
  /**
   * Mode of transportation
   * @example sea
   */
  modeOfTransportation?: string;
  /**
   * Name of the mode of transportation
   * @example Xin Chong Qing (9261128)
   */
  modeOfTransportationName?: string;
  /**
   * Terminal of the mode of transportation
   */
  terminal?: string;
  /**
   * Location of the tracking update.
   */
  location: string; // eslint-disable-line react/no-unused-prop-types
  /**
   * if there are exceptions in the tracking it will contain props
   * to display the exception component.
   */
  exceptionProps?: TrackingExceptionProps;
  /**
   * Number of pieces in the event (used in portcast air)
   */
  event_piece?: number;
  /**
   * Total piece (used in portcast air)
   */
  piece?: number; // eslint-disable-line react/no-unused-prop-types
  /**
   * Weight of the event (used in portcast air)
   */
  event_weight?: number;
  /**
   * Total weight (used in portcast air)
   */
  weight?: number; // eslint-disable-line react/no-unused-prop-types
  /**
   * Airline name (used in portcast air)
   */
  airline?: string; // eslint-disable-line react/no-unused-prop-types
  /**
   * Will be true if actual_arrival_date is null (used in portcast air)
   */
  isEstimatedDate?: boolean; // eslint-disable-line react/no-unused-prop-types
};

/**
 * Get the icon for the mode of transportation.
 *
 * @param modeOfTransportation - The mode of transportation (e.g., "vessel", "truck", "plane", "train")
 * @returns {ReactElement} - The corresponding icon for the mode of transportation
 */
export const getModeOfTransportationIcon = (modeOfTransportation: string | undefined): ReactNode => {
  switch (modeOfTransportation) {
    case "sea":
    case "vessel":
      return <CargoShipIcon className="tracking-history-item__mode-of-transportation-icon" />;
    case "truck":
      return <TruckIcon className="tracking-history-item__mode-of-transportation-icon" />;
    case "air":
    case "air_freight":
      return <PlaneIcon className="tracking-history-item__mode-of-transportation-icon" />;
    default:
      return null;
  }
};

const modeOfTransportationDisplayLabels: { [key: string]: string } = {
  sea: "Vessel",
  vessel: "Vessel",
  truck: "Truck",
};

const TrackingHistoryItem: FC<TrackingHistoryItemProps> = function (props) {
  const {
    date,
    message,
    modeOfTransportation,
    modeOfTransportationName,
    terminal,
    location,
    exceptionProps,
    event_piece,
    event_weight,
  } = props;

  const dateWithoutOffset = date?.slice(0, 19).replace("T", " "); // Remove the timezone offset as we do not want to convert the date to the user's timezone
  const timezoneMatch = date?.match(/([+-]\d{2}:\d{2})$/);
  const timezoneOffset = timezoneMatch ? timezoneMatch[0] : null;

  const formattedDate = !dateWithoutOffset
    ? ""
    : location !== "Not available" || !timezoneOffset
    ? format(new Date(dateWithoutOffset), "dd MMM yyyy, HH:mm")
    : `${format(new Date(dateWithoutOffset), "dd MMM yyyy, HH:mm")} (UTC ${timezoneOffset})`;
  const modeOfTransportationIcon = modeOfTransportation
    ? getModeOfTransportationIcon(modeOfTransportation.toLowerCase())
    : null;

  const modeOfTransportationLabel = modeOfTransportation
    ? modeOfTransportationDisplayLabels[modeOfTransportation.toLowerCase()]
    : undefined;

  return (
    <div className="tracking-history-item">
      <div className="d-flex align-items-center">
        <div className="tracking-history-item__date">{formattedDate}</div>
        {exceptionProps && (
          <span className="ml-sm">
            <TrackingException {...exceptionProps} />
          </span>
        )}
      </div>
      <div className="tracking-history-item__message">
        {ReactHtmlParser(replaceLinks(message || ""))}{" "}
        {modeOfTransportationLabel && (
          <span className="tracking-history-item__mode-of-transportation">• {modeOfTransportationLabel}</span>
        )}
      </div>

      <div className="d-flex align-items-center gap-xl tracking-history-item__additional-info">
        {modeOfTransportation && modeOfTransportationName && (
          <div className="d-flex align-items-center gap-sm">
            <div>{modeOfTransportationIcon}</div>
            <div>{modeOfTransportationName}</div>
          </div>
        )}
        <div className="d-flex gap-sm">
          {(event_piece || event_weight) && <PackageIcon />}
          <div>
            {event_piece && (
              <span>
                {event_piece} pc{event_piece === 1 ? "" : "s"} •{" "}
              </span>
            )}
            {event_weight && <span>{new Intl.NumberFormat().format(event_weight)} kg</span>}
          </div>
        </div>
      </div>

      {terminal && (
        <div>
          <span className="tracking-history-item__terminal-label">
            <FormattedMessage id="general.terminal" defaultMessage="Terminal" />:{" "}
          </span>
          <span className="tracking-history-item__terminal-name">{terminal}</span>
        </div>
      )}
    </div>
  );
};

TrackingHistoryItem.defaultProps = {
  modeOfTransportation: undefined,
  modeOfTransportationName: undefined,
  terminal: undefined,
  exceptionProps: undefined,
  event_piece: undefined,
  piece: undefined,
  event_weight: undefined,
  weight: undefined,
  airline: undefined,
  isEstimatedDate: undefined,
};

export default TrackingHistoryItem;
